import { useState, useEffect, useCallback, useRef } from 'react';
import tokenPriceStore from '../services/tokenPriceStore';

/**
 * Custom hook to fetch and manage token prices from CoinGecko with background updates
 * @param {Object} params - Hook parameters
 * @param {Object} params.provider - Web3 provider
 * @param {Array} params.tokenAddresses - Array of token addresses to fetch prices for
 * @param {number} params.refreshInterval - Refresh interval in milliseconds (default: 3 minutes)
 * @returns {Object} The token prices and status
 */
export const useTokenPrices = ({ 
  provider, 
  tokenAddresses = [], 
  refreshInterval = 180000 // 3 minutes (3 * 60 * 1000) default
}) => {
  // State for prices and loading status
  const [prices, setPrices] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  
  // Keep references to the latest props to avoid dependency issues
  const providerRef = useRef(provider);
  const tokenAddressesRef = useRef(tokenAddresses);
  const refreshIntervalRef = useRef(refreshInterval);
  const initialLoadCompletedRef = useRef(false);
  const fetchingRef = useRef(false);

  // Update refs when props change
  useEffect(() => {
    providerRef.current = provider;
    tokenAddressesRef.current = tokenAddresses;
    refreshIntervalRef.current = refreshInterval;

    // Add tokens to price store for monitoring when addresses change
    if (tokenAddresses && tokenAddresses.length > 0) {
      tokenPriceStore.addTokensToMonitor(tokenAddresses);
    }
  }, [provider, tokenAddresses, refreshInterval]);

  // Function to fetch token prices
  const fetchPrices = useCallback(async (force = false) => {
    // Prevent concurrent fetches
    if (fetchingRef.current && !force) {
      console.log('Price update already in progress, skipping');
      return;
    }
    
    // Get latest values from refs
    const currentProvider = providerRef.current;
    const currentTokenAddresses = tokenAddressesRef.current;
    
    // Skip if missing required data
    if (!currentTokenAddresses || currentTokenAddresses.length === 0) {
      return;
    }

    // Mark as fetching
    fetchingRef.current = true;
    setIsLoading(true);
    setError(null);
    
    try {
      // Filter out any null or undefined addresses
      const validAddresses = currentTokenAddresses.filter(addr => 
        addr && typeof addr === 'string' && addr.startsWith('0x')
      );
      
      // If we still have addresses after filtering, fetch prices
      if (validAddresses.length > 0) {
        console.log(`🔹 Fetching prices for ${validAddresses.length} tokens at ${new Date().toLocaleTimeString()}`);

        // Get prices from the token price store
        const newPrices = tokenPriceStore.getPrices(validAddresses);
        
        // Only update state if we received data
        if (newPrices && Object.keys(newPrices).length > 0) {
          setPrices(newPrices);
          setLastUpdated(new Date());
          
          console.log(`✅ Updated token prices at ${new Date().toLocaleTimeString()}`);
        }
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch token prices';
      console.error('❌ Error fetching token prices:', err);
      setError(errorMsg);
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, []); // Empty dependencies to make it stable

  // Initial load and setup of token prices - only run once!
  useEffect(() => {
    // Check if we've already loaded
    if (initialLoadCompletedRef.current) {
      return; // Skip if already loaded
    }

    // If we have token addresses, immediately try to get prices from the cache
    if (tokenAddresses && tokenAddresses.length > 0) {
      setIsLoading(true);

      try {
        // Add tokens to price store for monitoring
        tokenPriceStore.addTokensToMonitor(tokenAddresses);

        // Get cached prices (this should be nearly instant)
        const cachedPrices = tokenPriceStore.getPrices(tokenAddresses);

        // Check if we got any prices
        const hasPrices = Object.keys(cachedPrices).length > 0;

        if (hasPrices) {
          console.log('✅ Using cached prices from token store', cachedPrices);
          setPrices(cachedPrices);
          setLastUpdated(new Date());
        }

        // Trigger a background refresh regardless of whether we got cached prices or not
        // This ensures data is kept up to date but doesn't block the UI
        setTimeout(() => {
          fetchPrices(true);
        }, 100);

        // Mark initial load as complete
        initialLoadCompletedRef.current = true;
      } catch (err) {
        console.error('Error loading initial prices:', err);
      } finally {
        setIsLoading(false);
      }
    }
  }, [tokenAddresses, fetchPrices]); // We're using refs internally so these dependencies won't cause re-renders

  // Set up recurring price refreshes - controlled with interval
  useEffect(() => {
    // Skip frequent refreshes if tokens keep changing
    if (!initialLoadCompletedRef.current) {
      return;
    }

    console.log(`Setting up price refresh interval: ${refreshIntervalRef.current / 1000} seconds`);

    // Set up recurring refreshes (the background service will handle most updates)
    const intervalId = setInterval(() => {
      console.log('⏰ Scheduled price refresh triggered');
      fetchPrices(false);
    }, refreshIntervalRef.current);

    return () => {
      console.log('Clearing price refresh interval');
      clearInterval(intervalId);
    };
  }, [fetchPrices]); // fetchPrices is stable so this won't re-render

  // Listen for price updates from the price store
  useEffect(() => {
    const handlePriceUpdate = (data) => {
      const currentTokenAddresses = tokenAddressesRef.current;

      if (!currentTokenAddresses || currentTokenAddresses.length === 0) {
        return;
      }

      // Filter prices to only include our monitored tokens
      const relevantPrices = {};
      const normalizedAddresses = currentTokenAddresses.map(addr => addr.toLowerCase());

      normalizedAddresses.forEach(addr => {
        if (data.prices[addr] !== undefined) {
          relevantPrices[addr] = data.prices[addr];
        }
      });

      // Only update if we have relevant prices
      if (Object.keys(relevantPrices).length > 0) {
        setPrices(prev => ({ ...prev, ...relevantPrices }));
        setLastUpdated(new Date());
      }
    };

    // Add listener
    tokenPriceStore.addListener(handlePriceUpdate);

    // Cleanup
    return () => {
      tokenPriceStore.removeListener(handlePriceUpdate);
    };
  }, []); // Only set up once

  return {
    prices,
    isLoading,
    lastUpdated,
    error,
    refreshPrices: () => fetchPrices(true) // Force refresh when called manually
  };
};
